<template>
  <div class="page-parameter-interface">
    <!-- Navigation par onglets -->
    <ul class="nav nav-tabs mb-4" role="tablist">
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link" 
          :class="{ active: activeTab === 'layout' }"
          @click="selectTab('layout')"
          type="button"
        >
          <i class="fas fa-th-large me-2"></i>
          Mise en page
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          :class="{ active: activeTab === 'margins' }"
          @click="selectTab('margins')"
          type="button"
        >
          <i class="fas fa-expand-arrows-alt me-2"></i>
          Marges
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link" 
          :class="{ active: activeTab === 'stimulus' }"
          @click="selectTab('stimulus')"
          type="button"
        >
          <i class="fas fa-edit me-2"></i>
          Stimulus
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link" 
          :class="{ active: activeTab === 'theme' }"
          @click="selectTab('theme')"
          type="button"
        >
          <i class="fas fa-palette me-2"></i>
          Thème
        </button>
      </li>
    </ul>

    <!-- Contenu des onglets -->
    <div class="tab-content">
      
      <!-- Onglet Mise en page -->
      <div 
        class="tab-pane" 
        :class="{ 'active': activeTab === 'layout', 'd-none': activeTab !== 'layout' }"
      >
        <div class="row">
          <div class="col-md-6">
            <h6><i class="fas fa-columns me-2"></i>Disposition en colonnes</h6>
            <div class="form-check mb-2">
              <input 
                class="form-check-input" 
                type="radio" 
                name="columnLayout" 
                id="singleColumn"
                v-model="parameters.layout.columnType"
                value="single"
              >
              <label class="form-check-label" for="singleColumn">
                <i class="fas fa-square me-2"></i>Une colonne
              </label>
            </div>
            <div class="form-check mb-2">
              <input 
                class="form-check-input" 
                type="radio" 
                name="columnLayout" 
                id="twoColumns"
                v-model="parameters.layout.columnType"
                value="double"
              >
              <label class="form-check-label" for="twoColumns">
                <i class="fas fa-columns me-2"></i>Deux colonnes
              </label>
            </div>
            
            <!-- Options pour une colonne -->
            <div v-if="parameters.layout.columnType === 'single'" class="ms-4 mt-3">
              <label class="form-label">Largeur du contenu :</label>
              <select class="form-select" v-model="parameters.layout.singleColumnWidth">
                <option value="100">100% - Pleine largeur</option>
                <option value="80">80% - Largeur recommandée</option>
                <option value="70">70% - Largeur réduite</option>
                <option value="60">60% - Largeur étroite</option>
              </select>
              <small class="text-muted">Le test sera centré dans la page</small>
            </div>
            
            <!-- Options pour deux colonnes -->
            <div v-if="parameters.layout.columnType === 'double'" class="ms-4 mt-3">
              <label class="form-label">Répartition du contenu :</label>
              <select class="form-select" v-model="parameters.layout.contentDistribution">
                <option value="50-50">50% - 50%</option>
                <option value="30-70">30% - 70%</option>
                <option value="70-30">70% - 30%</option>
                <option value="40-60">40% - 60%</option>
                <option value="60-40">60% - 40%</option>
              </select>

              <!-- Option pour séparateur redimensionnable -->
              <div class="form-check mt-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="resizableSeparator"
                  v-model="parameters.layout.resizableSeparator"
                >
                <label class="form-check-label" for="resizableSeparator">
                  <i class="fas fa-arrows-alt-h me-2"></i>Séparateur ajustable par l'étudiant
                </label>
                <small class="d-block text-muted mt-1">
                  Affiche un séparateur visible avec poignée permettant à l'étudiant d'ajuster la largeur des colonnes
                </small>
              </div>
            </div>
          </div>
          
          <div class="col-md-6">
            <!-- Options d'affichage du stimulus déplacées ici -->
            <h6><i class="fas fa-cog me-2"></i>Options d'affichage du stimulus</h6>
            
            <div class="form-check mb-2">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="showStimulus"
                v-model="parameters.stimulus.visible"
              >
              <label class="form-check-label" for="showStimulus">
                <i class="fas fa-eye me-2"></i>Afficher le stimulus sur cette page
              </label>
            </div>
            
            <div v-if="parameters.stimulus.visible" class="ms-3">
              <div class="form-group mb-3">
                <label class="form-label">Position du stimulus :</label>
                <select class="form-select" v-model="parameters.stimulus.position">
                  <option value="top">En haut de la page</option>
                  <option value="left">À gauche des questions</option>
                  <option value="right">À droite des questions</option>
                  <option value="bottom">En bas de la page</option>
                </select>
              </div>
              
              <div class="form-check mb-2">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="stickyStimulus"
                  v-model="parameters.stimulus.sticky"
                >
                <label class="form-check-label" for="stickyStimulus">
                  <i class="fas fa-thumbtack me-2"></i>Stimulus fixe (reste visible lors du défilement)
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Aperçu déplacé en section séparée -->
        <div class="row mt-4">
          <div class="col-12">
            <h6><i class="fas fa-eye me-2"></i>Aperçu de la mise en page</h6>
            <p class="text-muted small mb-3">
              Voici comment le stimulus sera positionné par rapport aux questions selon vos paramètres.
            </p>
            
            <div class="layout-preview">
              <!-- Aperçu pour position top -->
              <div v-if="!parameters.stimulus.visible" class="preview-no-stimulus">
                <div class="preview-column single-column" style="width: 100%">
                  <span class="preview-label">Questions uniquement (pas de stimulus)</span>
                </div>
              </div>
              
              <div v-else-if="parameters.stimulus.position === 'top'" class="preview-stimulus-top">
                <div class="stimulus-preview-block">
                  <i class="fas fa-file-text me-2"></i>Stimulus
                  <span v-if="parameters.stimulus.sticky" class="ms-2 text-warning">
                    <i class="fas fa-thumbtack" title="Fixe"></i>
                  </span>
                </div>
                <div class="questions-preview-block">
                  <i class="fas fa-question-circle me-2"></i>Questions
                </div>
              </div>
              
              <!-- Aperçu pour position bottom -->
              <div v-else-if="parameters.stimulus.position === 'bottom'" class="preview-stimulus-bottom">
                <div class="questions-preview-block">
                  <i class="fas fa-question-circle me-2"></i>Questions
                </div>
                <div class="stimulus-preview-block">
                  <i class="fas fa-file-text me-2"></i>Stimulus
                  <span v-if="parameters.stimulus.sticky" class="ms-2 text-warning">
                    <i class="fas fa-thumbtack" title="Fixe"></i>
                  </span>
                </div>
              </div>
              
              <!-- Aperçu pour colonnes (left/right) -->
              <div v-else-if="parameters.stimulus.position === 'left' || parameters.stimulus.position === 'right'" class="preview-stimulus-columns">
                <div v-if="parameters.layout.columnType === 'double'" class="preview-double-layout">
                  <div
                    class="preview-column"
                    :class="{ 'stimulus-column': parameters.stimulus.position === 'left' }"
                    :style="{ width: getColumnWidth('left') }"
                  >
                    <span class="preview-label">
                      <i :class="parameters.stimulus.position === 'left' ? 'fas fa-file-text' : 'fas fa-question-circle'" class="me-2"></i>
                      {{ parameters.stimulus.position === 'left' ? 'Stimulus' : 'Questions' }}
                      <span v-if="parameters.stimulus.position === 'left' && parameters.stimulus.sticky" class="ms-2 text-warning">
                        <i class="fas fa-thumbtack" title="Fixe"></i>
                      </span>
                    </span>
                  </div>
                  <!-- Aperçu du séparateur si activé -->
                  <div v-if="parameters.layout.resizableSeparator" class="preview-separator">
                    <i class="fas fa-grip-lines-vertical"></i>
                  </div>
                  <div
                    class="preview-column"
                    :class="{ 'stimulus-column': parameters.stimulus.position === 'right' }"
                    :style="{ width: getColumnWidth('right') }"
                  >
                    <span class="preview-label">
                      <i :class="parameters.stimulus.position === 'right' ? 'fas fa-file-text' : 'fas fa-question-circle'" class="me-2"></i>
                      {{ parameters.stimulus.position === 'right' ? 'Stimulus' : 'Questions' }}
                      <span v-if="parameters.stimulus.position === 'right' && parameters.stimulus.sticky" class="ms-2 text-warning">
                        <i class="fas fa-thumbtack" title="Fixe"></i>
                      </span>
                    </span>
                  </div>
                </div>
                <div v-else class="alert alert-info">
                  <i class="fas fa-info-circle me-2"></i>
                  <strong>Configuration requise :</strong> La position "{{ parameters.stimulus.position === 'left' ? 'à gauche' : 'à droite' }}" nécessite une mise en page à deux colonnes. 
                  Sélectionnez "Deux colonnes" ci-dessus.
                </div>
              </div>
            </div>
            
            <!-- Informations contextuelles -->
            <div class="mt-3">
              <small class="text-muted">
                <i class="fas fa-lightbulb me-1"></i>
                <span v-if="!parameters.stimulus.visible">
                  Aucun stimulus ne sera affiché sur cette page.
                </span>
                <span v-else-if="parameters.stimulus.position === 'top'">
                  Le stimulus apparaîtra en haut de chaque page, au-dessus des questions.
                </span>
                <span v-else-if="parameters.stimulus.position === 'bottom'">
                  Le stimulus apparaîtra en bas de chaque page, après les questions.
                </span>
                <span v-else-if="parameters.stimulus.position === 'left'">
                  Le stimulus apparaîtra dans la colonne de gauche, les questions dans celle de droite.
                </span>
                <span v-else-if="parameters.stimulus.position === 'right'">
                  Le stimulus apparaîtra dans la colonne de droite, les questions dans celle de gauche.
                </span>
                <span v-if="parameters.stimulus.sticky && parameters.stimulus.visible">
                  Le stimulus restera visible lors du défilement de la page.
                </span>
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Onglet Marges -->
      <div
        class="tab-pane"
        :class="{ 'active': activeTab === 'margins', 'd-none': activeTab !== 'margins' }"
      >
        <div class="row">
          <div class="col-md-6">
            <h6><i class="fas fa-expand-arrows-alt me-2"></i>Configuration des marges</h6>
            <p class="text-muted small mb-4">
              Ajustez les marges pour optimiser l'affichage du contenu, notamment en mode 2 colonnes.
            </p>

            <!-- Presets de marges -->
            <div class="mb-4">
              <label class="form-label">Presets rapides :</label>
              <div class="btn-group w-100" role="group">
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="applyMarginPreset('none')">Aucune</button>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="applyMarginPreset('small')">Petites</button>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="applyMarginPreset('medium')">Moyennes</button>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="applyMarginPreset('large')">Grandes</button>
              </div>
            </div>

            <!-- Contrôles individuels des marges -->
            <div class="margins-controls">
              <!-- Marge haute -->
              <div class="margin-control-item mb-3">
                <label class="form-label">
                  <i class="fas fa-arrow-up me-2"></i>Marge supérieure : {{ parameters.margins.top }}px
                </label>
                <input
                  type="range"
                  class="form-range"
                  min="0"
                  max="100"
                  step="5"
                  v-model.number="parameters.margins.top"
                >
              </div>

              <!-- Marge gauche -->
              <div class="margin-control-item mb-3">
                <label class="form-label">
                  <i class="fas fa-arrow-left me-2"></i>Marge gauche : {{ parameters.margins.left }}px
                </label>
                <input
                  type="range"
                  class="form-range"
                  min="0"
                  max="100"
                  step="5"
                  v-model.number="parameters.margins.left"
                >
                <small class="text-muted">Utilisé pour ajuster l'espacement en mode 2 colonnes</small>
              </div>

              <!-- Marge droite -->
              <div class="margin-control-item mb-3">
                <label class="form-label">
                  <i class="fas fa-arrow-right me-2"></i>Marge droite : {{ parameters.margins.right }}px
                </label>
                <input
                  type="range"
                  class="form-range"
                  min="0"
                  max="100"
                  step="5"
                  v-model.number="parameters.margins.right"
                >
                <small class="text-muted">Utilisé pour ajuster l'espacement en mode 2 colonnes</small>
              </div>

              <!-- Marge basse -->
              <div class="margin-control-item mb-3">
                <label class="form-label">
                  <i class="fas fa-arrow-down me-2"></i>Marge inférieure : {{ parameters.margins.bottom }}px
                </label>
                <input
                  type="range"
                  class="form-range"
                  min="0"
                  max="100"
                  step="5"
                  v-model.number="parameters.margins.bottom"
                >
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <h6><i class="fas fa-eye me-2"></i>Aperçu des marges</h6>
            <div class="margin-preview">
              <div
                class="preview-page"
                :style="{
                  marginTop: parameters.margins.top + 'px',
                  marginBottom: parameters.margins.bottom + 'px',
                  marginLeft: parameters.margins.left + 'px',
                  marginRight: parameters.margins.right + 'px'
                }"
              >
                <div class="preview-content">
                  <i class="fas fa-file-alt me-2"></i>
                  Contenu de la page
                  <div class="mt-2">
                    <small class="text-muted">
                      Haut: {{ parameters.margins.top }}px |
                      Bas: {{ parameters.margins.bottom }}px<br>
                      Gauche: {{ parameters.margins.left }}px |
                      Droite: {{ parameters.margins.right }}px
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div class="alert alert-info mt-3">
              <i class="fas fa-info-circle me-2"></i>
              <strong>Conseil :</strong> En mode 2 colonnes, ajustez les marges gauche et droite pour optimiser l'espace entre les colonnes et améliorer la lisibilité.
            </div>
          </div>
        </div>
      </div>

      <!-- Onglet Stimulus -->
      <div 
        class="tab-pane" 
        :class="{ 'active': activeTab === 'stimulus', 'd-none': activeTab !== 'stimulus' }"
      >
        <div class="row">
          <div class="col-12">
            <h6><i class="fas fa-edit me-2"></i>Contenu du stimulus</h6>
            <p class="text-muted small mb-3">
              Créez le contenu du stimulus qui sera affiché aux étudiants. L'éditeur permet d'insérer du texte, des images, des liens et de la mise en forme.
            </p>
            
            <!-- Éditeur Quill -->
            <div class="quill-editor-container mb-4">
              <!-- Toolbar Quill personnalisée -->
              <div id="stimulus-toolbar">
                <!-- Formatage du texte -->
                <span class="ql-formats">
                  <select class="ql-header">
                    <option value="">Paragraphe</option>
                    <option value="1">Titre 1</option>
                    <option value="2">Titre 2</option>
                    <option value="3">Titre 3</option>
                  </select>
                  <select class="ql-size">
                    <option value="small">Petit</option>
                    <option value="">Normal</option>
                    <option value="large">Grand</option>
                    <option value="huge">Très grand</option>
                  </select>
                </span>
                
                <!-- Style de texte -->
                <span class="ql-formats">
                  <button class="ql-bold" title="Gras"></button>
                  <button class="ql-italic" title="Italique"></button>
                  <button class="ql-underline" title="Souligné"></button>
                  <button class="ql-strike" title="Barré"></button>
                </span>
                
                <!-- Couleurs -->
                <span class="ql-formats">
                  <select class="ql-color" title="Couleur du texte"></select>
                  <select class="ql-background" title="Couleur de fond"></select>
                </span>
                
                <!-- Listes et alignement -->
                <span class="ql-formats">
                  <button class="ql-list" value="ordered" title="Liste numérotée"></button>
                  <button class="ql-list" value="bullet" title="Liste à puces"></button>
                  <select class="ql-align" title="Alignement">
                    <option value="">Gauche</option>
                    <option value="center">Centré</option>
                    <option value="right">Droite</option>
                    <option value="justify">Justifié</option>
                  </select>
                </span>
                
                <!-- Médias et liens -->
                <span class="ql-formats">
                  <button class="ql-link" title="Insérer un lien"></button>
                  <button class="ql-image" title="Insérer une image"></button>
                  <button class="ql-video" title="Insérer une vidéo"></button>
                </span>
                
                <!-- Formatage avancé -->
                <span class="ql-formats">
                  <button class="ql-blockquote" title="Citation"></button>
                  <button class="ql-code-block" title="Bloc de code"></button>
                </span>
                
                <!-- Actions -->
                <span class="ql-formats">
                  <button class="ql-clean" title="Effacer la mise en forme"></button>
                </span>
              </div>
              
              <!-- Zone d'édition Quill -->
              <div 
                ref="quillEditor"
                class="quill-editor-content"
                style="min-height: 250px; background: white;"
              >
                <!-- Le contenu sera initialisé par Quill -->
              </div>
            </div>
            
            <!-- Informations sur les options -->
            <div class="stimulus-info">
              <div class="alert alert-info">
                <i class="fas fa-info-circle me-2"></i>
                <strong>Options d'affichage déplacées :</strong> Les paramètres de visibilité, position et comportement du stimulus se trouvent maintenant dans l'onglet <strong>"Mise en page"</strong> pour une meilleure organisation.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Onglet Thème -->
      <div 
        class="tab-pane" 
        :class="{ 'active': activeTab === 'theme', 'd-none': activeTab !== 'theme' }"
      >
        <div class="row">
          <div class="col-md-6">
            <h6><i class="fas fa-palette me-2"></i>Thème de la page</h6>
            <p class="text-muted small mb-4">
              Choisissez le thème d'affichage pour améliorer la lisibilité selon les préférences des étudiants.
            </p>
            
            <!-- Options de thème simplifiées -->
            <div class="theme-selection mb-4">
              <div class="form-check mb-3">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  name="themeMode" 
                  id="lightTheme"
                  v-model="parameters.theme.mode"
                  value="light"
                >
                <label class="form-check-label theme-label" for="lightTheme">
                  <div class="theme-option light-option">
                    <i class="fas fa-sun theme-icon"></i>
                    <div class="theme-details">
                      <h6>Thème clair</h6>
                      <small class="text-muted">Fond blanc, texte sombre - recommandé pour la lecture</small>
                    </div>
                  </div>
                </label>
              </div>
              
              <div class="form-check mb-3">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  name="themeMode" 
                  id="darkTheme"
                  v-model="parameters.theme.mode"
                  value="dark"
                >
                <label class="form-check-label theme-label" for="darkTheme">
                  <div class="theme-option dark-option">
                    <i class="fas fa-moon theme-icon"></i>
                    <div class="theme-details">
                      <h6>Thème sombre</h6>
                      <small class="text-muted">Fond sombre, texte clair - réduit la fatigue oculaire</small>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          <div class="col-md-6">
            <h6><i class="fas fa-eye me-2"></i>Aperçu du thème</h6>
            <div 
              class="theme-preview-container"
              :class="{
                'dark-preview': parameters.theme.mode === 'dark',
                'light-preview': parameters.theme.mode === 'light'
              }"
            >
              <div class="preview-header">
                <h5>{{ parameters.theme.mode === 'dark' ? '🌙' : '☀️' }} Aperçu {{ parameters.theme.mode === 'dark' ? 'sombre' : 'clair' }}</h5>
              </div>
              <div class="preview-content">
                <h6>Question d'exemple :</h6>
                <p>Voici comment apparaîtra le texte des questions avec le thème {{ parameters.theme.mode === 'dark' ? 'sombre' : 'clair' }}.</p>
                
                <div class="example-question">
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="example" id="exampleA">
                    <label class="form-check-label" for="exampleA">
                      Option A - Première réponse possible
                    </label>
                  </div>
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="example" id="exampleB">
                    <label class="form-check-label" for="exampleB">
                      Option B - Deuxième réponse possible
                    </label>
                  </div>
                </div>
                
                <div class="btn-group mt-3" role="group">
                  <button type="button" class="btn btn-outline-secondary btn-sm">Précédent</button>
                  <button type="button" class="btn btn-primary btn-sm">Suivant</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions en bas -->
    <div class="parameter-actions mt-4 pt-3 border-top">
      <div class="row align-items-center">
        <div class="col-md-7">
          <h6 class="mb-3">
            <i class="fas fa-layer-group me-2"></i>
            Portée d'application
          </h6>
          
          <div class="form-check mb-2">
            <input 
              class="form-check-input" 
              type="radio" 
              name="applyScope" 
              id="currentPageOnly"
              v-model="applyScope"
              value="current"
            >
            <label class="form-check-label" for="currentPageOnly">
              <i class="fas fa-file me-2"></i>
              Page actuelle uniquement
            </label>
          </div>
          
          <div class="form-check mb-2">
            <input 
              class="form-check-input" 
              type="radio" 
              name="applyScope" 
              id="nextPagesOnly"
              v-model="applyScope"
              value="next"
            >
            <label class="form-check-label" for="nextPagesOnly">
              <i class="fas fa-arrow-right me-2"></i>
              Appliquer aux pages suivantes
            </label>
          </div>
          
          <div class="form-check mb-2">
            <input 
              class="form-check-input" 
              type="radio" 
              name="applyScope" 
              id="allPages"
              v-model="applyScope"
              value="all"
            >
            <label class="form-check-label" for="allPages">
              <i class="fas fa-globe me-2"></i>
              Appliquer à toutes les pages du test
            </label>
          </div>
          
          <!-- Info sur la portée sélectionnée -->
          <div class="mt-2">
            <small class="text-muted">
              <span v-if="applyScope === 'current'">
                <i class="fas fa-info-circle me-1"></i>
                Les paramètres ne seront appliqués qu'à cette page.
              </span>
              <span v-else-if="applyScope === 'next'">
                <i class="fas fa-info-circle me-1"></i>
                Les paramètres seront appliqués à toutes les pages après celle-ci.
              </span>
              <span v-else-if="applyScope === 'all'">
                <i class="fas fa-info-circle me-1"></i>
                Les paramètres seront appliqués à toutes les pages du test (y compris cette page).
              </span>
            </small>
          </div>
        </div>
        
        <div class="col-md-5 text-end">
          <button type="button" class="btn btn-secondary me-2" @click="resetParameters">
            <i class="fas fa-undo me-2"></i>
            Réinitialiser
          </button>
          <button type="button" class="btn btn-primary" @click="applyParameters">
            <i class="fas fa-check me-2"></i>
            {{ getApplyButtonText() }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useTestStore } from '../stores/testStore';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

// Props
const props = defineProps({
  pageData: {
    type: Object,
    required: true
  }
});

// Émissions
const emit = defineEmits(['close', 'apply']);

// Store Pinia
const store = useTestStore();

// État des onglets
const activeTab = ref('layout');

// Debug pour vérifier l'état des onglets
console.log('🎛️ Onglet actif initial:', activeTab.value);
console.log('🎛️ Page data reçue:', props.pageData);

// Paramètres connectés aux vraies données
const parameters = ref({
  layout: {
    columnType: 'single',
    singleColumnWidth: 80,
    contentDistribution: '50-50',
    resizableSeparator: false
  },
  // Conservé pour éviter les erreurs même si l'onglet est masqué
  margins: {
    top: 20,
    bottom: 20,
    left: 20,
    right: 20
  },
  stimulus: {
    content: '',
    visible: true,
    sticky: false,
    position: 'top'
  },
  theme: {
    mode: 'light'
  }
});

// Portée d'application (current, next, all)
const applyScope = ref('current');

// Référence pour l'éditeur Quill
const quillEditor = ref(null);
const quillInstance = ref(null);

// Charger les paramètres de la page au montage
onMounted(() => {
  loadPageParameters();
  // Initialiser Quill après le premier rendu si l'onglet stimulus est visible
  nextTick(() => {
    if (activeTab.value === 'stimulus') {
      initializeQuillEditor();
    }
  });
});

// Recharger les paramètres si la page change
watch(() => props.pageData, () => {
  loadPageParameters();
}, { deep: true });

// Fonction pour charger les paramètres de la page
const loadPageParameters = () => {
  if (!props.pageData?.id) {
    console.warn('🎛️ Aucune page fournie, utilisation des paramètres par défaut');
    return;
  }

  console.log('🎛️ Chargement des paramètres pour la page:', props.pageData.id);
  
  // Récupérer les paramètres effectifs de la page (avec fusion des défauts)
  const effectiveParameters = store.getPageParameters(props.pageData.id);
  
  console.log('🎛️ Paramètres effectifs chargés:', effectiveParameters);
  
  // Mettre à jour l'interface en forçant la réactivité
  parameters.value = JSON.parse(JSON.stringify(effectiveParameters));
  
  // Mettre à jour le contenu Quill si l'éditeur est initialisé
  if (quillInstance.value && effectiveParameters.stimulus?.content) {
    quillInstance.value.root.innerHTML = effectiveParameters.stimulus.content;
  }
};

// Initialiser l'éditeur Quill
const initializeQuillEditor = () => {
  if (!quillEditor.value) {
    console.warn('🖋️ Element Quill non trouvé');
    return;
  }

  console.log('🖋️ Initialisation de l\'éditeur Quill');

  // Configuration de Quill
  const quillConfig = {
    theme: 'snow',
    modules: {
      toolbar: {
        container: '#stimulus-toolbar',
        handlers: {
          // Gestionnaire personnalisé pour les images
          image: imageHandler,
          // Gestionnaire personnalisé pour les vidéos
          video: videoHandler
        }
      },
      history: {
        delay: 1000,
        maxStack: 50,
        userOnly: true
      }
    },
    formats: [
      'header', 'size', 'bold', 'italic', 'underline', 'strike',
      'color', 'background', 'list', 'align',
      'link', 'image', 'video', 'blockquote', 'code-block',
      'iframe', 'div'
    ]
  };

  // Créer l'instance Quill
  quillInstance.value = new Quill(quillEditor.value, quillConfig);

  // Charger le contenu existant
  if (parameters.value.stimulus?.content) {
    quillInstance.value.root.innerHTML = parameters.value.stimulus.content;
  } else {
    // Contenu par défaut
    quillInstance.value.root.innerHTML = `
      <h3>Stimulus de la page</h3>
      <p>Rédigez ici le contenu du stimulus qui sera présenté aux étudiants.</p>
      <p>Vous pouvez utiliser tous les outils de mise en forme disponibles dans la barre d'outils.</p>
    `;
  }

  // Écouter les changements de contenu
  quillInstance.value.on('text-change', () => {
    const content = quillInstance.value.root.innerHTML;
    parameters.value.stimulus.content = content;
    console.log('🖋️ Contenu Quill mis à jour');
  });

  console.log('✅ Éditeur Quill initialisé avec succès');
};

// Gestionnaire personnalisé pour l'insertion d'images
const imageHandler = () => {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();

  input.onchange = () => {
    const file = input.files[0];
    if (file) {
      // Vérifier la taille du fichier (limite à 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        alert('L\'image est trop grande. Veuillez choisir une image de moins de 2MB.');
        return;
      }

      // Conversion en Base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        
        // Vérifier que l'éditeur Quill est toujours disponible
        if (!quillInstance.value) {
          console.error('Instance Quill non disponible');
          return;
        }

        try {
          // Approche alternative : manipuler directement le HTML du contenu
          const currentContent = quillInstance.value.root.innerHTML;
          const imageHtml = `<p><img src="${base64}" style="max-width: 100%; height: auto;"/></p><p><br></p>`;
          
          // Ajouter l'image à la fin du contenu existant
          const newContent = currentContent + imageHtml;
          
          // Mettre à jour le contenu
          quillInstance.value.root.innerHTML = newContent;
          
          // Déclencher l'événement de changement pour mettre à jour les paramètres
          quillInstance.value.emitter.emit('text-change');
          
          // Mettre à jour les paramètres manuellement
          parameters.value.stimulus.content = newContent;
          
          console.log('✅ Image insérée avec succès via manipulation HTML');
          
        } catch (error) {
          console.error('❌ Erreur lors de l\'insertion de l\'image:', error);
          console.error('❌ Détails de l\'erreur:', error.stack);
          
          // Méthode de fallback : ajouter directement au contenu des paramètres
          try {
            const fallbackImageHtml = `<p><img src="${base64}" style="max-width: 100%; height: auto;"/></p>`;
            parameters.value.stimulus.content += fallbackImageHtml;
            
            // Recharger le contenu dans Quill
            if (quillInstance.value && quillInstance.value.root) {
              quillInstance.value.root.innerHTML = parameters.value.stimulus.content;
            }
            
            console.log('✅ Image insérée avec succès via méthode de fallback');
          } catch (fallbackError) {
            console.error('❌ Erreur même avec la méthode de fallback:', fallbackError);
            alert('Erreur lors de l\'insertion de l\'image. L\'éditeur semble avoir un problème.');
          }
        }
      };
      
      reader.onerror = () => {
        console.error('❌ Erreur lors de la lecture du fichier');
        alert('Erreur lors de la lecture du fichier image.');
      };
      
      reader.readAsDataURL(file);
    }
  };
};

// Gestionnaire personnalisé pour l'insertion de vidéos avec contrôle de taille
const videoHandler = () => {
  // Créer une modal personnalisée pour l'insertion de vidéo
  const modalHtml = `
    <div id="video-modal" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      z-index: 2000;
      display: flex;
      justify-content: center;
      align-items: center;
    ">
      <div style="
        background: white;
        padding: 25px;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        max-width: 500px;
        width: 90%;
      ">
        <h4 style="margin: 0 0 20px 0; color: #333;">Insérer une vidéo</h4>
        
        <div style="margin-bottom: 15px;">
          <label style="display: block; margin-bottom: 5px; font-weight: bold;">URL de la vidéo :</label>
          <input type="url" id="video-url" placeholder="https://www.youtube.com/watch?v=..." style="
            width: 100%;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 14px;
            box-sizing: border-box;
          ">
        </div>
        
        <div style="margin-bottom: 20px;">
          <label style="display: block; margin-bottom: 5px; font-weight: bold;">Taille de la vidéo :</label>
          <select id="video-size" style="
            width: 100%;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 14px;
          ">
            <option value="small">Petite (400px)</option>
            <option value="medium" selected>Moyenne (600px)</option>
            <option value="large">Grande (800px)</option>
            <option value="full">Pleine largeur</option>
          </select>
        </div>
        
        <div style="text-align: right;">
          <button id="video-cancel" style="
            background: #6c757d;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            margin-right: 10px;
            cursor: pointer;
          ">Annuler</button>
          <button id="video-insert" style="
            background: #007bff;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            cursor: pointer;
          ">Insérer</button>
        </div>
      </div>
    </div>
  `;

  // Ajouter la modal au DOM
  document.body.insertAdjacentHTML('beforeend', modalHtml);
  
  const modal = document.getElementById('video-modal');
  const urlInput = document.getElementById('video-url');
  const sizeSelect = document.getElementById('video-size');
  const cancelBtn = document.getElementById('video-cancel');
  const insertBtn = document.getElementById('video-insert');

  // Focus sur le champ URL
  urlInput.focus();

  // Fonction pour fermer la modal
  const closeModal = () => {
    modal.remove();
  };

  // Gestionnaire d'annulation
  cancelBtn.onclick = closeModal;
  modal.onclick = (e) => {
    if (e.target === modal) closeModal();
  };

  // Gestionnaire d'insertion
  insertBtn.onclick = () => {
    const url = urlInput.value.trim();
    console.log('🎬 URL saisie:', url);
    
    if (!url) {
      alert('Veuillez entrer une URL de vidéo');
      return;
    }

    const size = sizeSelect.value;
    console.log('📏 Taille sélectionnée:', size);
    
    try {
      // Vérifier que l'instance Quill est disponible
      if (!quillInstance.value) {
        console.error('❌ Instance Quill non disponible');
        alert('Erreur: éditeur non disponible');
        return;
      }

      // Convertir l'URL en embed si nécessaire
      const embedUrl = convertToEmbedUrl(url);
      console.log('🔗 URL convertie:', embedUrl);
      
      // Créer le HTML de la vidéo avec la classe de taille
      const sizeClass = size === 'full' ? '' : `video-${size}`;
      const videoHtml = `<div class="video-container ${sizeClass}"><iframe 
        src="${embedUrl}" 
        frameborder="0" 
        allowfullscreen 
        allow="picture-in-picture" 
        style="width:100%;height:300px;border-radius:8px;"
        loading="lazy"
        referrerpolicy="strict-origin-when-cross-origin"
      ></iframe></div><p><br></p>`;
      
      console.log('🎞️ HTML généré:', videoHtml);
      
      // Ajouter la vidéo au contenu (même méthode que pour les images)
      const currentContent = quillInstance.value.root.innerHTML;
      console.log('📄 Contenu actuel:', currentContent);
      
      const newContent = currentContent + videoHtml;
      console.log('📄 Nouveau contenu:', newContent);
      
      // Approche simplifiée : utiliser un placeholder pour les vidéos
      // Quill filtre les iframes, donc on utilise un élément qu'il accepte
      
      // Extraire un titre plus court pour l'affichage
      let displayTitle = url;
      if (url.includes('youtube.com/watch?v=')) {
        const videoId = url.split('v=')[1].split('&')[0];
        displayTitle = `YouTube: ${videoId}`;
      } else if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1].split('?')[0];
        displayTitle = `YouTube: ${videoId}`;
      } else if (url.includes('vimeo.com/')) {
        const videoId = url.split('vimeo.com/')[1].split('?')[0];
        displayTitle = `Vimeo: ${videoId}`;
      } else if (url.length > 50) {
        displayTitle = url.substring(0, 47) + '...';
      }
      
      const videoPlaceholder = `<p data-video-url="${embedUrl}" data-video-size="${size}" style="
        background: #f8f9fa; 
        border: 2px dashed #007bff; 
        padding: 20px; 
        text-align: center; 
        border-radius: 8px; 
        margin: 10px 0;
        color: #007bff;
        font-weight: bold;
      ">📹 VIDÉO: ${displayTitle} (${size})</p>`;
      
      // Ajouter le placeholder au contenu
      const newContentWithPlaceholder = currentContent + videoPlaceholder;
      
      // Mettre à jour Quill avec le placeholder
      quillInstance.value.root.innerHTML = newContentWithPlaceholder;
      
      // Mettre à jour les paramètres avec le vrai HTML vidéo pour l'affichage final
      parameters.value.stimulus.content = newContent;
      
      // Déclencher l'événement de changement
      quillInstance.value.emitter.emit('text-change');
      
      console.log('✅ Placeholder vidéo ajouté dans Quill');
      console.log('✅ HTML vidéo sauvé dans les paramètres pour le rendu final');
      
      console.log('✅ Vidéo insérée avec succès en taille:', size);
      console.log('✅ Paramètres mis à jour:', parameters.value.stimulus.content);
      
      closeModal();
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'insertion de la vidéo:', error);
      console.error('❌ Stack trace:', error.stack);
      alert('Erreur lors de l\'insertion de la vidéo: ' + error.message);
    }
  };

  // Gérer la touche Entrée
  urlInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      insertBtn.click();
    }
  });
};

// Fonction pour convertir les URLs YouTube/Vimeo en URLs embed
const convertToEmbedUrl = (url) => {
  console.log('🔗 URL originale à convertir:', url);
  
  // YouTube - différentes variantes d'URL
  if (url.includes('youtube.com/watch?v=')) {
    const videoId = url.split('v=')[1].split('&')[0];
    // Ajouter des paramètres pour éviter les erreurs et publicités
    const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=0&controls=1&showinfo=0`;
    console.log('🎬 YouTube URL convertie:', embedUrl);
    return embedUrl;
  }
  
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1].split('?')[0];
    const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=0&controls=1&showinfo=0`;
    console.log('🎬 YouTube short URL convertie:', embedUrl);
    return embedUrl;
  }
  
  // YouTube embed déjà formatée - améliorer les paramètres
  if (url.includes('youtube.com/embed/')) {
    const baseUrl = url.split('?')[0]; // Enlever les paramètres existants
    const embedUrl = `${baseUrl}?rel=0&modestbranding=1&autoplay=0&controls=1&showinfo=0`;
    console.log('🎬 YouTube embed améliorée:', embedUrl);
    return embedUrl;
  }
  
  // Vimeo
  if (url.includes('vimeo.com/')) {
    const videoId = url.split('vimeo.com/')[1].split('?')[0];
    const embedUrl = `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`;
    console.log('🎬 Vimeo URL convertie:', embedUrl);
    return embedUrl;
  }
  
  // Si c'est déjà une URL embed ou autre, la retourner telle quelle
  console.log('🔗 URL retournée telle quelle:', url);
  return url;
};

// Méthodes
const selectTab = (tabName) => {
  console.log('🎛️ Changement d\'onglet vers:', tabName);
  activeTab.value = tabName;
  console.log('🎛️ Onglet actif maintenant:', activeTab.value);
  
  // Initialiser Quill si on sélectionne l'onglet Stimulus et que Quill n'est pas encore initialisé
  if (tabName === 'stimulus' && !quillInstance.value) {
    nextTick(() => {
      initializeQuillEditor();
    });
  }
};

const getColumnWidth = (position) => {
  const dist = parameters.value.layout.contentDistribution;
  const [left, right] = dist.split('-');
  return position === 'left' ? left + '%' : right + '%';
};


const applyMarginPreset = (preset) => {
  const presets = {
    none: { top: 0, bottom: 0, left: 0, right: 0 },
    small: { top: 10, bottom: 10, left: 10, right: 10 },
    medium: { top: 20, bottom: 20, left: 20, right: 20 },
    large: { top: 40, bottom: 40, left: 40, right: 40 }
  };
  
  Object.assign(parameters.value.margins, presets[preset]);
};

const getApplyButtonText = () => {
  switch (applyScope.value) {
    case 'current':
      return 'Appliquer à cette page';
    case 'next':
      return 'Appliquer aux pages suivantes';
    case 'all':
      return 'Appliquer à toutes les pages';
    default:
      return 'Appliquer';
  }
};

const resetParameters = () => {
  console.log('🔄 Remise à zéro des paramètres de page');
  
  if (props.pageData?.id) {
    // Supprimer les paramètres personnalisés de la page dans le store
    store.resetPageParameters(props.pageData.id);
    
    // Recharger les paramètres par défaut dans l'interface
    loadPageParameters();
  } else {
    console.warn('⚠️ Impossible de réinitialiser: aucune page fournie');
  }
  
  applyScope.value = 'current';
};

const applyParameters = () => {
  console.log('🎛️ Application des paramètres:', parameters.value);
  console.log('🎛️ Portée d\'application:', applyScope.value);
  console.log('🎛️ Layout avant application:', parameters.value.layout);
  console.log('🎛️ MARGINS avant application:', parameters.value.margins);

  if (!props.pageData?.id) {
    console.error('❌ Impossible d\'appliquer: aucune page fournie');
    return;
  }

  // Créer une copie profonde pour éviter les problèmes de référence
  const parametersToApply = JSON.parse(JSON.stringify(parameters.value));
  console.log('🎛️ Paramètres à appliquer (copie):', parametersToApply);
  console.log('🎛️ MARGINS à appliquer (copie):', parametersToApply.margins);

  // Appliquer les paramètres via le store selon la portée
  store.applyPageParameters({
    parameters: parametersToApply,
    scope: applyScope.value,
    pageId: props.pageData.id
  });

  // Émettre aussi vers le parent pour notification
  emit('apply', {
    parameters: parametersToApply,
    scope: applyScope.value,
    pageId: props.pageData.id
  });

  console.log('✅ Paramètres appliqués avec succès');

  // Recharger les paramètres pour vérifier qu'ils ont bien été sauvés
  setTimeout(() => {
    const reloaded = store.getPageParameters(props.pageData.id);
    console.log('🔄 Paramètres rechargés depuis le store:', reloaded);
    console.log('🔄 MARGINS rechargés:', reloaded.margins);
    loadPageParameters();
  }, 200);

  // Fermer la modal
  emit('close');
};
</script>

<style scoped>
.page-parameter-interface {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Onglets */
.nav-tabs {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 1.5rem;
}

.nav-tabs .nav-link {
  border: none;
  color: #6c757d;
  background: none;
  padding: 0.75rem 1rem;
}

.nav-tabs .nav-link.active {
  color: #007bff;
  border-bottom: 2px solid #007bff;
  background: none;
  font-weight: 500;
}

.nav-tabs .nav-link:hover {
  border: none;
  color: #007bff;
  background: rgba(0, 123, 255, 0.1);
}

/* Contenu des onglets */
.tab-content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 1rem;
}

.tab-pane {
  padding: 1rem 0;
  height: 100%;
}

/* Aperçu de layout */
.layout-preview {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  background: #f8f9fa;
  min-height: 120px;
}

.preview-double,
.preview-single {
  display: flex;
  gap: 10px;
  height: 80px;
}

.preview-column {
  background: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 0.875rem;
}

.preview-column.stimulus-column {
  background: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.preview-column.single-column {
  transition: width 0.3s ease;
}

.preview-column.full-width {
  width: 100%;
}

/* Configuration des marges */
.margins-grid {
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 1rem;
  max-width: 400px;
}

.margin-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.margins-horizontal {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1rem;
  align-items: center;
}

.margin-content {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 0.375rem;
  padding: 2rem;
  min-height: 100px;
}

.content-preview {
  color: #6c757d;
  font-style: italic;
}

.margin-preview {
  border: 2px solid #007bff;
  border-radius: 0.375rem;
  padding: 1rem;
  background: #f8f9fa;
  position: relative;
  overflow: hidden;
}

.preview-page {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.preview-content {
  padding: 1rem;
  text-align: center;
  color: #6c757d;
  font-size: 0.875rem;
}

/* Éditeur Quill */
.quill-editor-container {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Toolbar Quill personnalisée */
#stimulus-toolbar {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0.75rem;
}

#stimulus-toolbar .ql-formats {
  margin-right: 15px;
}

#stimulus-toolbar .ql-formats:last-child {
  margin-right: 0;
}

/* Zone d'édition Quill */
.quill-editor-content {
  border: none;
}

/* Styles spécifiques pour l'éditeur Quill */
.ql-editor {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 16px;
  line-height: 1.6;
}

.ql-editor h1, .ql-editor h2, .ql-editor h3 {
  margin-bottom: 0.5rem;
}

.ql-editor p {
  margin-bottom: 1rem;
}

.ql-editor blockquote {
  border-left: 4px solid #007bff;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.25rem;
}

.ql-editor ul, .ql-editor ol {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

/* Placeholder pour l'éditeur vide */
.ql-editor.ql-blank::before {
  color: #6c757d;
  content: attr(data-placeholder);
  font-style: italic;
}

/* 🎯 Correction du positionnement des tooltips/popups Quill */
.ql-tooltip {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 1000 !important;
  background: white !important;
  border: 1px solid #ccc !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
  padding: 15px !important;
  min-width: 300px !important;
  max-width: 90vw !important;
  /* Empêcher le débordement sur les côtés */
  margin-left: auto !important;
  margin-right: auto !important;
}

/* Ajustement spécifique pour les petits écrans */
@media (max-width: 400px) {
  .ql-tooltip {
    left: 5% !important;
    right: 5% !important;
    transform: translate(0, -50%) !important;
    width: 90% !important;
    min-width: unset !important;
  }
}

/* Styles pour le popup vidéo spécifiquement */
.ql-tooltip.ql-editing {
  background: white !important;
}

.ql-tooltip input[type="text"] {
  width: calc(100% - 120px) !important;
  max-width: 250px !important;
  min-width: 180px !important;
  padding: 8px 12px !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  margin-right: 10px !important;
  box-sizing: border-box !important;
}

.ql-tooltip input[type="text"]:focus {
  outline: none !important;
  border-color: #007bff !important;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25) !important;
}

/* Boutons du tooltip */
.ql-tooltip .ql-action,
.ql-tooltip .ql-remove {
  background: #007bff !important;
  color: white !important;
  border: none !important;
  padding: 6px 12px !important;
  border-radius: 4px !important;
  cursor: pointer !important;
  font-size: 12px !important;
  margin-left: 5px !important;
}

.ql-tooltip .ql-remove {
  background: #dc3545 !important;
}

.ql-tooltip .ql-action:hover {
  background: #0056b3 !important;
}

.ql-tooltip .ql-remove:hover {
  background: #c82333 !important;
}

/* Flèche du tooltip (la cacher car elle ne s'adapte pas au centrage) */
.ql-tooltip::before {
  display: none !important;
}

/* S'assurer que le tooltip reste visible */
.ql-tooltip.ql-hidden {
  display: none !important;
}

.ql-tooltip:not(.ql-hidden) {
  display: block !important;
}

/* 📹 Contrôle de la taille des vidéos dans Quill */
.ql-editor iframe,
.ql-editor video {
  max-width: 100% !important;
  height: auto !important;
  min-height: 200px !important;
  max-height: 400px !important;
  width: 100% !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  margin: 10px 0 !important;
}

/* Styles spécifiques pour les iframes YouTube/Vimeo */
.ql-editor iframe[src*="youtube.com"],
.ql-editor iframe[src*="youtu.be"],
.ql-editor iframe[src*="vimeo.com"] {
  aspect-ratio: 16/9 !important;
  height: auto !important;
  min-height: unset !important;
  max-height: unset !important;
}

/* Container pour les vidéos avec contrôles de redimensionnement */
.ql-editor .video-container {
  position: relative !important;
  display: inline-block !important;
  max-width: 100% !important;
  margin: 10px 0 !important;
}

/* Styles pour différentes tailles prédéfinies */
.ql-editor .video-small iframe,
.ql-editor .video-small video {
  max-width: 400px !important;
  max-height: 225px !important;
}

.ql-editor .video-medium iframe,
.ql-editor .video-medium video {
  max-width: 600px !important;
  max-height: 338px !important;
}

.ql-editor .video-large iframe,
.ql-editor .video-large video {
  max-width: 800px !important;
  max-height: 450px !important;
}

.stimulus-options {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

/* 🎭 Aperçu de mise en page dans l'onglet Stimulus */
.stimulus-layout-preview {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  background: #f8f9fa;
  min-height: 120px;
}

.preview-stimulus-top,
.preview-stimulus-bottom {
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100px;
}

.preview-stimulus-columns .preview-double-layout {
  display: flex;
  gap: 0;
  height: 80px;
}

/* Aperçu du séparateur dans l'aperçu */
.preview-separator {
  width: 8px;
  background-color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 10px;
  flex-shrink: 0;
  border-radius: 2px;
  margin: 0 5px;
}

.preview-double-layout .preview-column {
  flex-shrink: 0;
}

.stimulus-preview-block,
.questions-preview-block {
  background: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem;
}

.stimulus-preview-block {
  background: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.questions-preview-block {
  flex: 1;
}

/* Réutilisation des styles de preview-column existants */
.stimulus-layout-preview .preview-column {
  background: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 0.875rem;
}

.stimulus-layout-preview .preview-column.stimulus-column {
  background: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

/* Thèmes simplifiés */
.theme-selection {
  max-width: 500px;
}

.theme-label {
  cursor: pointer;
  margin-bottom: 0;
  width: 100%;
}

.theme-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  background: white;
}

.theme-option:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.form-check-input:checked + .theme-label .theme-option {
  border-color: #007bff;
  background: #f8f9fa;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.theme-icon {
  font-size: 1.5rem;
  width: 40px;
  text-align: center;
}

.light-option .theme-icon {
  color: #ffc107;
}

.dark-option .theme-icon {
  color: #6f42c1;
}

.theme-details h6 {
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
}

.theme-details small {
  font-size: 0.875rem;
  line-height: 1.3;
}

/* Aperçu du thème */
.theme-preview-container {
  border: 2px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
  min-height: 300px;
}

.theme-preview-container.light-preview {
  background: white;
  color: #212529;
  border-color: #ffc107;
}

.theme-preview-container.dark-preview {
  background: #212529;
  color: white;
  border-color: #6f42c1;
}

.theme-preview-container .preview-header {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.05);
}

.theme-preview-container.dark-preview .preview-header {
  background: rgba(255, 255, 255, 0.05);
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.theme-preview-container .preview-header h5 {
  margin: 0;
  font-size: 1.1rem;
}

.theme-preview-container .preview-content {
  padding: 1.5rem;
}

.theme-preview-container .preview-content h6 {
  margin: 0 0 1rem 0;
  font-weight: 600;
}

.theme-preview-container .preview-content p {
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.example-question {
  background: rgba(0, 0, 0, 0.03);
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}

.theme-preview-container.dark-preview .example-question {
  background: rgba(255, 255, 255, 0.05);
}

.example-question .form-check {
  margin-bottom: 0.5rem;
}

.example-question .form-check-label {
  font-size: 0.95rem;
}

/* Actions en pied de modal */
.parameter-actions {
  background: #f8f9fa;
  margin: 0 -1.5rem -1.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
}
</style>